import * as THREE from 'three';

export interface WaterOptions {
  width: number;
  height: number;
  deepColor: number;
  shallowColor: number;
  flowSpeed?: number;
}

export class StylizedWater {
  public mesh: THREE.Mesh;
  private material: THREE.ShaderMaterial;
  private timeUniform: { value: number };

  constructor(options: WaterOptions) {
    const geo = new THREE.PlaneGeometry(options.width, options.height, 48, 48);
    geo.rotateX(-Math.PI / 2);

    this.timeUniform = { value: 0 };

    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: this.timeUniform,
        uDeepColor: { value: new THREE.Color(options.deepColor) },
        uShallowColor: { value: new THREE.Color(options.shallowColor) },
        uSunDirection: { value: new THREE.Vector3(0.5, 0.8, 0.3).normalize() },
      },
      vertexShader: `
        uniform float uTime;
        varying vec3 vWorldPosition;
        varying vec2 vUv;
        varying vec3 vNormalVec;

        void main() {
          vUv = uv;
          vec3 pos = position;

          // Stylized gentle sinusoidal ripples
          float wave1 = sin(pos.x * 0.4 + uTime * 1.5) * cos(pos.z * 0.4 + uTime * 1.2) * 0.15;
          float wave2 = sin(pos.x * 0.8 - uTime * 2.0 + pos.z * 0.6) * 0.08;
          pos.y += wave1 + wave2;

          vec4 worldPos = modelMatrix * vec4(pos, 1.0);
          vWorldPosition = worldPos.xyz;

          // Approximate normal for stylized lighting
          float dx = cos(pos.x * 0.4 + uTime * 1.5) * 0.06;
          float dz = -sin(pos.z * 0.4 + uTime * 1.2) * 0.06;
          vNormalVec = normalize(vec3(-dx, 1.0, -dz));

          gl_Position = projectionMatrix * viewMatrix * worldPos;
        }
      `,
      fragmentShader: `
        uniform vec3 uDeepColor;
        uniform vec3 uShallowColor;
        uniform vec3 uSunDirection;
        uniform float uTime;
        varying vec3 vWorldPosition;
        varying vec2 vUv;
        varying vec3 vNormalVec;

        void main() {
          // Foam pattern on edge
          float foam = sin(vUv.x * 30.0 + uTime) * sin(vUv.y * 30.0 - uTime);
          foam = smoothstep(0.7, 0.95, foam) * 0.25;

          // Gentle specular glint
          vec3 viewDir = normalize(cameraPosition - vWorldPosition);
          vec3 halfVec = normalize(uSunDirection + viewDir);
          float spec = pow(max(dot(vNormalVec, halfVec), 0.0), 32.0) * 0.45;

          // Depth blend based on radial distance
          float dist = length(vUv - 0.5) * 2.0;
          vec3 col = mix(uDeepColor, uShallowColor, smoothstep(0.2, 0.9, dist));
          col += vec3(foam);
          col += vec3(spec);

          gl_FragColor = vec4(col, 0.88);
        }
      `,
      transparent: true,
      depthWrite: false,
    });

    this.mesh = new THREE.Mesh(geo, this.material);
    this.mesh.receiveShadow = true;
  }

  public update(dt: number): void {
    this.timeUniform.value += dt;
  }

  public dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}
